'use client';

import React, { useState, KeyboardEvent } from 'react';
import { X } from 'lucide-react';

interface TagInputProps {
  tags: string[];
  onTagsChange: (tags: string[]) => void;
  maxTags?: number;
}

export default function TagInput({ tags, onTagsChange, maxTags = 10 }: TagInputProps) {
  const [inputValue, setInputValue] = useState('');

  const addTag = (raw: string) => {
    const tag = raw.trim().toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    if (!tag || tags.includes(tag) || tags.length >= maxTags) return;
    onTagsChange([...tags, tag]);
    setInputValue('');
  };

  const removeTag = (tag: string) => {
    onTagsChange(tags.filter((t) => t !== tag));
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag(inputValue);
    } else if (e.key === 'Backspace' && !inputValue && tags.length > 0) {
      removeTag(tags[tags.length - 1]);
    }
  };

  return (
    <div
      className="input-field px-3 py-2 flex flex-wrap gap-1.5 min-h-[48px] cursor-text"
      onClick={(e) => {
        const input = (e.currentTarget as HTMLDivElement).querySelector('input');
        input?.focus();
      }}
    >
      {tags.map((tag) => (
        <span
          key={`tag-chip-${tag}`}
          className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium"
          style={{
            background: 'rgba(108, 99, 255, 0.15)',
            color: 'var(--primary)',
            border: '1px solid rgba(108, 99, 255, 0.3)',
          }}
        >
          #{tag}
          <button
            type="button"
            onClick={() => removeTag(tag)}
            className="hover:opacity-70 transition-opacity"
          >
            <X size={10} />
          </button>
        </span>
      ))}
      {tags.length < maxTags && (
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={() => { if (inputValue) addTag(inputValue); }}
          placeholder={tags.length === 0 ? 'cinematic, neon, slowmo...' : ''}
          className="flex-1 bg-transparent outline-none text-sm min-w-[120px]"
          style={{ color: 'var(--foreground)' }}
        />
      )}
      <span className="ml-auto text-xs self-center flex-shrink-0" style={{ color: 'var(--muted-foreground)' }}>
        {tags.length}/{maxTags}
      </span>
    </div>
  );
}